import { IngotIcon } from "@/ingot";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";

import {
  usePublicOnboardingCarriers,
  usePublicOnboardingCatalog,
} from "@/api/queries/publicOnboarding";
import {
  activeBlockers,
  SubmitBlockers,
  useSubmitGuard,
} from "@/components/ui/SubmitGuard";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { useSeo } from "@/lib/seo";
import { cx } from "@/lib/utils";
import { BlocksStep } from "@/pages/onboarding/wizard/BlocksStep";
import { ChoicesAside } from "@/pages/onboarding/wizard/ChoicesAside";
import { DeliveryStep } from "@/pages/onboarding/wizard/DeliveryStep";
import {
  readStoredDraft,
  writeStoredDraft,
  type WizardDraft,
} from "@/pages/onboarding/wizard/draft";
import { ONBOARDING_RAIL, type WizardCarrier } from "@/pages/onboarding/wizard/model";
import { OperationsStep } from "@/pages/onboarding/wizard/OperationsStep";
import { stepHeading } from "@/pages/onboarding/wizard/StepHeading";
import {
  selectionRules,
  toWizardDraft,
  useWizardSelection,
} from "@/pages/onboarding/wizard/useWizardSelection";
import { PlatformSamplePrice } from "@/components/platform-landing/PlatformSamplePrice";

import "./OnboardingStart.css";

const STEPS = ["operations", "blocks", "delivery", "preview"] as const;
type Step = (typeof STEPS)[number];

// KAN-1944: the lead said "three steps, the same as a real start" above a
// four-stop rail, while the real onboarding has more. Both counts are read
// from the rails themselves: the tryout's stops before its preview, and the
// real wizard's whole rail.
const TRIED_STEPS = STEPS.filter((id) => id !== "preview").length;

const NO_CARRIERS: WizardCarrier[] = [];

export default function PlatformTryout(): JSX.Element {
  const { t } = useTranslation("common");
  const { t: tWizard } = useTranslation("admin");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = langParam && isSupportedLang(langParam) ? langParam : DEFAULT_LANG;
  useSeo({
    title: t("platformTryout.seo.title"),
    description: t("platformTryout.seo.description"),
    path: "/try",
    lang,
  });

  const [search, setSearch] = useSearchParams();
  const guard = useSubmitGuard();

  const catalogQuery = usePublicOnboardingCatalog(lang);
  const catalog = catalogQuery.data ?? null;
  const w = useWizardSelection({ catalog, customOptions: null });
  const { chosen, countryCodes, setCarrierCodes, restore } = w;
  const carriersQuery = usePublicOnboardingCarriers(countryCodes, lang);
  const carriers =
    countryCodes.length > 0 ? (carriersQuery.data ?? NO_CARRIERS) : NO_CARRIERS;

  // The stored draft comes back ONCE, as soon as there is an offer to read it
  // through; what the catalog no longer offers is named, not dropped silently.
  const restored = useRef(false);
  const [dropped, setDropped] = useState<string[]>([]);
  useEffect(() => {
    if (restored.current || catalog === null) return;
    restored.current = true;
    const stored = readStoredDraft();
    if (stored) setDropped(restore(stored, catalog));
  }, [catalog, restore]);

  // A carrier picked for a country since unticked is no longer on offer.
  useEffect(() => {
    if (!carriersQuery.data) return;
    const offered = new Set(carriersQuery.data.map((c) => c.code));
    setCarrierCodes((current) =>
      current.every((code) => offered.has(code))
        ? current
        : current.filter((code) => offered.has(code)),
    );
  }, [carriersQuery.data, setCarrierCodes]);

  // Rewritten on every change — but only once the stored one was read, or
  // the first render's empty selection would erase it. Compared as text: the
  // selection object is new on every render, its content is not.
  const draftText = JSON.stringify(toWizardDraft(w));
  useEffect(() => {
    if (restored.current) writeStoredDraft(JSON.parse(draftText) as WizardDraft);
  }, [draftText]);

  const rulesFor = (current: Step) =>
    current === "preview" ? [] : selectionRules(current, w, carriers, tWizard);
  // The end screen is earned: an unanswered step is where a deep link lands.
  const firstOpen = STEPS.find((id) => activeBlockers(rulesFor(id)).length > 0) ?? "preview";
  const requested = search.get("step");
  const wanted = STEPS.find((id) => id === requested) ?? STEPS[0];
  const step: Step = STEPS.indexOf(wanted) <= STEPS.indexOf(firstOpen) ? wanted : firstOpen;
  const stepIndex = STEPS.indexOf(step);
  const goTo = (next: Step) => {
    setSearch({ step: next }, { replace: false });
    window.scrollTo({ top: 0 });
  };
  const advance = () => {
    if (!guard.check(...rulesFor(step))) return;
    goTo(STEPS[stepIndex + 1]);
  };

  const stillFailing = new Set(activeBlockers(rulesFor(step)));
  const shownBlockers = guard.blockers.filter((message) => stillFailing.has(message));

  return (
    <main className="rz mx-auto w-full max-w-6xl px-5 pt-8" data-testid="platform-tryout">
      <div className="wiz wiz-in-page">
        <div className="wiz-top">
          <h1>{t("platformTryout.title")}</h1>
          <p>
            {t("platformTryout.lead", {
              count: TRIED_STEPS,
              total: ONBOARDING_RAIL.length,
            })}
          </p>
        </div>

        <nav className="rail" data-testid="platform-tryout-rail">
          {STEPS.map((id, i) => (
            <button
              key={id}
              type="button"
              disabled={i > STEPS.indexOf(firstOpen)}
              onClick={() => goTo(id)}
              aria-current={i === stepIndex ? "step" : undefined}
              className={cx(i === stepIndex && "is-active", i < stepIndex && "is-done")}
            >
              <i>{i < stepIndex ? <IngotIcon name="check" size={12} /> : i + 1}</i>
              <span>
                <b>
                  {id === "preview"
                    ? t("platformTryout.rail.preview.name")
                    : tWizard(`onboardingStart.rail.${id}.name`)}
                </b>
                <span>
                  {id === "preview"
                    ? t("platformTryout.rail.preview.desc")
                    : tWizard(`onboardingStart.rail.${id}.desc`)}
                </span>
              </span>
            </button>
          ))}
        </nav>

        {dropped.length > 0 ? (
          <div className="zero" data-testid="platform-tryout-dropped">
            <IngotIcon name="info" size={18} />
            <div className="t-small">
              {t("platformTryout.dropped", { items: dropped.join(", ") })}
            </div>
          </div>
        ) : null}

        <div className="wiz-grid">
          <div className="stack" style={{ gap: 22 }}>
            {catalogQuery.isError ? (
              <div className="zero" data-testid="platform-tryout-catalog-error">
                <IngotIcon name="info" size={18} />
                <div className="t-small">{t("platformTryout.catalogError")}</div>
              </div>
            ) : null}

            {step === "operations" ? (
              <OperationsStep
                w={w}
                catalog={catalog}
                customOptions={null}
                allowStartEmpty={false}
              />
            ) : null}

            {step === "blocks" ? <BlocksStep w={w} allowCustom={false} /> : null}

            {step === "delivery" ? (
              <DeliveryStep w={w} catalog={catalog} carriers={carriers} lang={lang} />
            ) : null}

            {step === "preview" ? (
              <>
                {stepHeading(t("platformTryout.preview.title"), t("platformTryout.preview.sub"))}
                {chosen.map((op) => <PlatformSamplePrice key={op.slug} operation={op}
                  machines={w.choices[op.slug]?.machines ?? []}
                  materialCodes={w.choices[op.slug]?.materialCodes ?? []} lang={lang} />)}
                <section className="opblock" data-testid="platform-tryout-next">
                  <div className="opblock-body stack">
                    <b>{t("platformTryout.next.title")}</b>
                    <p className="t-small">{t("platformTryout.next.body")}</p>
                    <div className="row">
                      <Link
                        to={`/${lang}/sign-up-shop?from=try`}
                        className="btn btn-primary btn-lg"
                        data-testid="platform-tryout-signup"
                      >
                        {t("platformTryout.next.signup")} <IngotIcon name="arrow-right" />
                      </Link>
                      <Link to={`/${lang}/contact`} className="btn btn-ghost">
                        {t("platformTryout.next.contact")}
                      </Link>
                    </div>
                  </div>
                </section>
              </>
            ) : null}
          </div>

          <ChoicesAside w={w} carriers={carriers} />
        </div>

        {stepIndex > 0 || step !== "preview" ? (
          <div className="wiz-foot">
            <div className="wiz-foot-in">
              {stepIndex > 0 ? (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => goTo(STEPS[stepIndex - 1])}
                  data-testid="platform-tryout-back"
                >
                  <IngotIcon name="arrow-right" className="rotate-180" />{" "}
                  {tWizard("onboardingStart.back")}
                </button>
              ) : null}
              {step !== "preview" && !(catalog !== null && catalog.operations.length === 0) ? (
                <button
                  type="button"
                  className="btn btn-primary btn-lg"
                  onClick={advance}
                  data-testid="platform-tryout-continue"
                >
                  {tWizard("onboardingStart.continue")} <IngotIcon name="arrow-right" />
                </button>
              ) : null}
              {shownBlockers.length > 0 ? (
                <div style={{ marginRight: "auto" }}>
                  <SubmitBlockers blockers={shownBlockers} />
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </main>
  );
}
